/**
 * Vesper DM / Group Chat Service (Shared)
 */

import apiClient from './api.client';
import { API } from '../constants/api';
import { deduped, bust } from './dedupedFetch';
import type { DMConversation, DMMessage, DMConversationMember } from '../types/dm.types';

export async function startOrOpenDM(targetUserId: string): Promise<{ conversationId: string }> {
  const res = await apiClient.post<{ data: { conversationId: string } }>(API.DM_START, { targetUserId });
  // A new 1:1 DM may be created — invalidate the conversation list so the
  // user sees it on next read.
  bust('me:dmConversations');
  return res.data.data;
}

export async function createGroupChat(memberIds: string[], name?: string): Promise<{ conversationId: string }> {
  const res = await apiClient.post<{ data: { conversationId: string } }>(API.DM_CREATE_GROUP, { memberIds, name });
  bust('me:dmConversations');
  return res.data.data;
}

export async function getDMConversations(): Promise<DMConversation[]> {
  return deduped('me:dmConversations', async () => {
    const res = await apiClient.get<{ data: DMConversation[] }>(API.DM_CONVERSATIONS);
    return res.data.data;
  });
}

export async function getDMMessages(conversationId: string, before?: string, limit?: number): Promise<DMMessage[]> {
  const parts: string[] = [];
  if (before) parts.push(`before=${before}`);
  if (limit) parts.push(`limit=${limit}`);
  const qs = parts.length ? `?${parts.join('&')}` : '';
  const res = await apiClient.get<{ data: DMMessage[] }>(`${API.DM_MESSAGES(conversationId)}${qs}`);
  return res.data.data;
}

export async function sendDMMessage(conversationId: string, content: string): Promise<DMMessage> {
  const res = await apiClient.post<{ data: DMMessage }>(API.DM_SEND(conversationId), { content });
  // lastMessage / ordering of conversation list changes — invalidate.
  bust('me:dmConversations');
  return res.data.data;
}

export async function getDMMembers(conversationId: string): Promise<DMConversationMember[]> {
  const res = await apiClient.get<{ data: DMConversationMember[] }>(API.DM_MEMBERS(conversationId));
  return res.data.data;
}

export async function leaveConversation(conversationId: string): Promise<void> {
  await apiClient.delete(API.DM_LEAVE(conversationId));
}
